<template>
  <div class="wc-tab">
    <load-state :state="state" empty-text="暂无资讯" @retry="refresh">
      <div class="wc-news-list">
        <button
          v-for="item in newsList"
          :key="item.id"
          class="wc-news-card"
          :class="{ 'is-no-image': !hasNewsImage(item) }"
          @click="goNews(item.id)"
        >
          <div v-if="hasNewsImage(item)" class="wc-news-card__thumb">
            <img v-if="newsImage(item.img) && !imageFailed[item.id]" :src="newsImage(item.img)" :alt="item.title" @error="markFailed(item.id)">
          </div>
          <div class="wc-news-card__content">
            <div class="wc-news-card__title">{{ item.title }}</div>
            <div class="wc-news-card__meta">
              <span>{{ item.news_source }}</span>
              <em>{{ newsTime(item.publish_time) }}</em>
            </div>
          </div>
        </button>
      </div>
      <button v-if="hasMore" class="wc-news-more" :disabled="loadingMore" @click="loadMore">
        {{ loadingMore ? '加载中...' : '加载更多' }}
      </button>
      <div v-else-if="newsList.length" class="wc-news-end">没有更多数据了</div>
    </load-state>
  </div>
</template>

<script>
import { getNews } from '@/api/worldcup2026'
import LoadState from '../components/LoadState.vue'
import { formatNewsTime } from '../utils/time'
import { worldcupCache } from '../utils/cache'
import { normalizeImageUrl } from '../utils/image'

export default {
  name: 'WorldCupNewsTab',
  components: { LoadState },
  data () {
    return {
      state: 'idle',
      page: 1,
      pageSize: 10,
      newsList: [],
      hasMore: true,
      loadingMore: false,
      imageFailed: {}
    }
  },
  created () {
    this.loadPage(1, true)
  },
  methods: {
    newsTime (value) {
      return formatNewsTime(value)
    },
    newsImage (url) {
      return normalizeImageUrl(url)
    },
    hasNewsImage (item) {
      return !!this.newsImage(item && item.img) && !this.imageFailed[item.id]
    },
    markFailed (id) {
      this.$set(this.imageFailed, id, true)
    },
    mergeNewsList (current, incoming) {
      const seen = {}
      return current.concat(incoming).filter(item => {
        const id = item && item.id
        if (!id || seen[id]) return false
        seen[id] = true
        return true
      })
    },
    async refresh () {
      worldcupCache.newsPages = {}
      this.page = 1
      this.newsList = []
      this.hasMore = true
      await this.loadPage(1, false)
    },
    async loadMore () {
      if (!this.hasMore || this.loadingMore) return
      await this.loadPage(this.page + 1, true)
    },
    async loadPage (page, useCache) {
      if (page === 1) this.state = 'loading'
      this.loadingMore = true
      try {
        const beforeCount = this.newsList.length
        let list = worldcupCache.newsPages[page]
        if (!useCache || !list) {
          const resp = await getNews({ page, page_size: this.pageSize })
          if (resp.error_code !== 0) {
            this.state = 'error'
            return
          }
          list = resp.result && resp.result.data ? resp.result.data : []
          worldcupCache.newsPages[page] = list
        }
        if (page === 1) {
          this.newsList = this.mergeNewsList([], list)
        } else {
          this.newsList = this.mergeNewsList(this.newsList, list)
        }
        const addedCount = this.newsList.length - beforeCount
        this.page = page
        this.hasMore = list.length >= this.pageSize && (page === 1 || addedCount > 0)
        this.state = this.newsList.length ? 'success' : 'empty'
      } catch (e) {
        this.state = 'error'
      } finally {
        this.loadingMore = false
      }
    },
    goNews (id) {
      const query = this.$route.query.screen ? { screen: this.$route.query.screen } : {}
      this.$router.push({ path: `/worldcup2026/news/${id}`, query })
    }
  }
}
</script>

<style scoped lang="scss">
.wc-tab {
  padding: 12px;
}

.wc-news-card {
  display: flex;
  width: 100%;
  min-height: 94px;
  margin-bottom: 10px;
  padding: 10px;
  border: 0;
  border-radius: 8px;
  background: #ffffff;
  text-align: left;
}

.wc-news-card__thumb {
  width: 96px;
  height: 74px;
  flex: 0 0 96px;
  border-radius: 6px;
  background: #e5e7eb;
  overflow: hidden;
}

.wc-news-card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wc-news-card__content {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
}

.wc-news-card.is-no-image {
  min-height: 72px;
  padding: 12px;
}

.wc-news-card.is-no-image .wc-news-card__content {
  margin-left: 0;
}

.wc-news-card.is-no-image .wc-news-card__title {
  min-height: 0;
}

.wc-news-card__title {
  min-height: 40px;
  color: #1a1a1a;
  font-size: 15px;
  line-height: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.wc-news-card__meta {
  display: flex;
  align-items: center;
  margin-top: 12px;
  color: #9aa0a6;
  font-size: 12px;
}

.wc-news-card__meta span {
  max-width: 92px;
  padding: 2px 6px;
  border-radius: 10px;
  color: #b07d00;
  background: #fff7df;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-news-card__meta em {
  margin-left: 8px;
  font-style: normal;
}

.wc-news-more {
  display: block;
  width: 120px;
  height: 34px;
  margin: 14px auto 8px;
  border: 0;
  border-radius: 17px;
  box-sizing: border-box;
  color: #5a4300;
  font-size: 14px;
  line-height: 34px;
  text-align: center;
  background: #f6c343;
}

.wc-news-more:disabled {
  color: #9a7d29;
  background: #f8dc88;
}

.wc-news-end {
  height: 34px;
  margin: 14px auto 8px;
  color: #9aa0a6;
  font-size: 13px;
  line-height: 34px;
  text-align: center;
}
</style>
